import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type CustomDomainStatus =
  | 'pending_validation'
  | 'issued'
  | 'attached'
  | 'failed'
  | 'detached';

export type CustomDomainValidationRecord = {
  name: string;
  type: string;
  value: string;
};

export type CustomDomain = {
  id: string;
  domain: string;
  status: CustomDomainStatus;
  validationRecords: CustomDomainValidationRecord[];
  errorMessage?: string | null;
  lastCheckedAt?: string | null;
  createdAt: string;
};

export type CustomDomainsData = {
  /** Org's vanity-URL prefix ({orgSlug}-{slug}.{envDomain}) */
  orgSlug: string;
  /** Platform sandbox base domain (null when unavailable, e.g. local dev) */
  envDomain: string | null;
  /** Environment ALB DNS name — target of the customer's wildcard CNAME */
  albDnsName: string | null;
  /** Whether the org has the custom-domains feature flag */
  customDomainsEnabled: boolean;
  domains: CustomDomain[];
};

/**
 * Custom-domain list + vanity-URL context. While any domain is pending
 * validation the list refetches every 30s so the status chip advances
 * without a manual check.
 */
export function useCustomDomains(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['custom-domains'],
    queryFn: async (): Promise<{ ok: boolean; data: CustomDomainsData }> => {
      const res = await fetch(`${API_URL}/org/environments/custom-domains`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch custom domains');
      return res.json();
    },
    enabled: options?.enabled ?? true,
    // The payload is near-static (org slug is immutable, domains change on
    // explicit admin action) — don't refetch on every dialog open.
    staleTime: 30_000,
    refetchInterval: (query) => {
      const pending = query.state.data?.data?.domains?.some(
        (d) => d.status === 'pending_validation' || d.status === 'issued'
      );
      return pending ? 30_000 : false;
    },
  });
}

export function useRegisterCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domain: string) => {
      const res = await fetch(`${API_URL}/org/environments/custom-domains`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to register custom domain');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}

export function useCheckCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string) => {
      const res = await fetch(
        `${API_URL}/org/environments/custom-domains/${domainId}/check`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to check custom domain');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}

export function useDeleteCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string) => {
      const res = await fetch(`${API_URL}/org/environments/custom-domains/${domainId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to remove custom domain');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}
